import { useEffect, useState } from "react";
import { Link } from "react-router";
import { productModelService } from "../../Api/Services/productModelService";
import { ProductMissingAttributesResponse } from "../../Models/Product";
import { ProductModel } from "../../Models/ProductModel";
import { FaSearch, FaTimes } from "react-icons/fa";
import Modal from "../Modal/Modal";

type ProductAnalysisModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data: ProductMissingAttributesResponse | null;
  isLoading: boolean;
};

const ProductAnalysisModal = ({
  isOpen,
  onClose,
  data,
  isLoading
}: ProductAnalysisModalProps) => {
  const hasCorreiosContract = data?.hasCorreiosContract ?? false;
  const products = data?.products ?? [];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModelId, setSelectedModelId] = useState<string>("all");
  const [models, setModels] = useState<ProductModel[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isMounted = true;

    const loadModels = async () => {
      try {
        const response = await productModelService.getAll();
        if (isMounted) {
          setModels(response);
        }
      } catch {
        if (isMounted) {
          setModels([]);
        }
      }
    };

    loadModels();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedModelId("all");
  };

  const handleClose = () => {
    resetFilters();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const modelNameById = new Map(models.map((model) => [model.id, model.name]));
  const modelOptions: { id: string; name: string }[] = [];
  const productModelIds = new Set<string>();
  let hasNoModel = false;

  for (const product of products) {
    if (product.modelId) {
      productModelIds.add(product.modelId);
    } else {
      hasNoModel = true;
    }
  }

  for (const model of models) {
    if (productModelIds.has(model.id)) {
      modelOptions.push({ id: model.id, name: modelNameById.get(model.id) ?? model.name });
    }
  }

  modelOptions.sort((a, b) => a.name.localeCompare(b.name));
  if (hasNoModel) {
    modelOptions.unshift({ id: "none", name: "N/I" });
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      normalizedSearch.length === 0 ||
      product.name.toLowerCase().includes(normalizedSearch) ||
      product.code.toLowerCase().includes(normalizedSearch);

    const matchesModel =
      selectedModelId === "all" ||
      (selectedModelId === "none" && !product.modelId) ||
      product.modelId === selectedModelId;

    return matchesSearch && matchesModel;
  });

  return (
    <Modal overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-md shadow-xl px-6 py-8 relative w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-primary-600 hover:text-primary-700 text-2xl font-bold"
          disabled={isLoading}
        >
          <FaTimes />
        </button>

        <h3 className="text-lg font-bold text-primary-600 mb-4">{"Analisar produtos"}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {"Confira os produtos que ainda precisam de informações obrigatórias para venda."}
        </p>

        {hasCorreiosContract && (
          <div className="mb-4 text-sm text-gray-600">
            <span className="font-semibold text-gray-700">{"Correios"}:</span> {"peso e dimensões são obrigatórios."}
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-gray-600">{"Analisando produtos..."}</div>
        ) : products.length === 0 ? (
          <div className="text-sm text-gray-600">{"Nenhum produto com dados pendentes."}</div>
        ) : (
          <>
            <div className="border border-gray-200 rounded-md p-4 mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4">{"Filtros"}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{"Pesquisa"}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={"Nome ou código"}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm pr-9"
                    />
                    <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{"Modelo"}</label>
                  <select
                    value={selectedModelId}
                    onChange={(event) => setSelectedModelId(event.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                  >
                    <option value="all">{"Todos os modelos"}</option>
                    {modelOptions.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-sm text-gray-600">{"Nenhum produto encontrado com os filtros selecionados."}</div>
            ) : (
              <div className="space-y-4">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="border border-gray-200 rounded-md p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">
                          {product.name}
                        </div>
                        <div className="text-xs text-gray-500">{"Código"}: {product.code}</div>
                        <div className="text-xs text-gray-500">
                          {"Modelo"}: {product.modelId ? (modelNameById.get(product.modelId) ?? "Modelo não encontrado") : "N/I"}
                        </div>
                      </div>
                      <Link
                        to={`/admin/produtos/${product.id}`}
                        className="text-primary-600 text-sm font-medium hover:text-primary-700"
                      >
                        {"Editar produto"}
                      </Link>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.missingAttributes.map((missing) => (
                        <span
                          key={missing}
                          className="text-xs font-semibold text-primary-700 bg-primary-120 px-2 py-1 rounded-full"
                        >
                          {missing}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProductAnalysisModal;
